import React, { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import ProductImage from './ProductImage';
import { useToast } from '../context/ToastContext';
import EmptyState from './EmptyState';

function CartDrawer({ isOpen, onClose }) {
  const { cart, removeFromCart, updateQuantity, moveCartToWishlist, loading } = useCart();
  const drawerRef = useRef(null);
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  const CART_DRAWER_ANIM_MS = 250;
  const [isRendered, setIsRendered] = React.useState(isOpen);
  const [isClosing, setIsClosing] = React.useState(false);

  // Close when clicking outside
  useEffect(() => {
    if (isOpen) {
      setIsRendered(true);
      setIsClosing(false);
    } else if (isRendered) {
      setIsClosing(true);
      const t = window.setTimeout(() => {
        setIsRendered(false);
        setIsClosing(false);
      }, CART_DRAWER_ANIM_MS);
      return () => window.clearTimeout(t);
    }
  }, [isOpen, isRendered]);

  useEffect(() => {
    const shouldLock = isRendered;
    const handleClickOutside = (event) => {
      if (!shouldLock) return;
      if (drawerRef.current && !drawerRef.current.contains(event.target)) onClose();
    };

    if (shouldLock) {
      document.addEventListener('mousedown', handleClickOutside);
      // Lock body scroll
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isRendered, onClose]);

  const handleCheckout = () => {
    onClose();
    navigate('/checkout');
  };

  const handleViewCart = () => {
    onClose();
    navigate('/cart');
  };

  // Prevent rendering if closed and animation complete
  if (!isRendered) return null;

  return (
    <>
      <div
        className={`cart-drawer-backdrop ${isClosing ? 'cart-drawer-backdrop--closing' : 'cart-drawer-backdrop--opening'}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={`cart-drawer ${isClosing ? 'cart-drawer--closing' : 'cart-drawer--opening'}`}
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Shopping Cart"
      >
        <div className="cart-drawer-header">
          <h2 className="cart-drawer-title">
            <i className="bi bi-cart3 me-2"></i>
            Your Cart {cart.count > 0 && <span>({cart.count})</span>}
          </h2>
          <button className="cart-drawer-close" onClick={onClose} aria-label="Close cart">
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <div className="cart-drawer-body">
          {cart.items.length === 0 ? (
            <div className="cart-drawer-empty">
              <EmptyState
                variant="cart"
                title="Your cart is empty"
                message="Add essentials you need today. Your cart will stay here while you browse."
                actionLabel="Browse products"
                actionLink="/products"
                className="cart-drawer-empty-state"
              >
                <div className="cart-drawer-empty-actions" role="group" aria-label="Next steps">
                  <button
                    className="btn btn-outline-secondary w-100"
                    type="button"
                    onClick={onClose}
                  >
                    Keep browsing
                  </button>
                  <Link
                    className="btn btn-link w-100 cart-drawer-empty-link"
                    to="/wishlist"
                    onClick={onClose}
                  >
                    View wishlist
                  </Link>
                </div>
              </EmptyState>
            </div>
          ) : (
            <>
              {/* Progress bar for free shipping ($100 threshold) */}
              <div className="cart-drawer-shipping-progress mb-4">
                {cart.total >= 100 ? (
                  <div className="shipping-success">
                    <i className="bi bi-check-circle-fill me-2"></i>
                    You've unlocked <strong>Free Shipping!</strong>
                  </div>
                ) : (
                  <div>
                    <div className="d-flex justify-content-between text-sm mb-1">
                      <span>Add £{(100 - cart.total).toFixed(2)} for Free Shipping</span>
                    </div>
                    <div className="progress" style={{ height: '8px' }}>
                      <div className="progress-bar bg-warning" role="progressbar" style={{ width: `${(cart.total / 100) * 100}%` }} aria-valuenow={(cart.total / 100) * 100} aria-valuemin="0" aria-valuemax="100"></div>
                    </div>
                  </div>
                )}
              </div>

              <div className="cart-drawer-items">
                {cart.items.map((item, index) => {
                  const product = item.product || item.productId || {};
                  const productId = product._id || item.productId || index;
                  const imageSrc = item.image || product.images?.main;
                  const name = item.name || product.name || 'Item';

                  return (
                    <div key={productId} className="cart-drawer-item">
                      <div className="cart-drawer-item-img">
                        <ProductImage
                          src={imageSrc}
                          alt={name}
                          className="img-fluid rounded"
                        />
                      </div>
                      <div className="cart-drawer-item-info">
                        <h4 className="cart-drawer-item-title">
                          <Link to={`/product/${productId}`} onClick={onClose}>
                            {name}
                          </Link>
                        </h4>
                        <div className="cart-drawer-item-price">£{item.price.toFixed(2)}</div>
                        <div className="cart-drawer-item-actions mt-2">
                          <div className="cart-quantity-pill qty-sm" aria-label="Adjust quantity">
                            <button
                              type="button"
                              className="qty-pill-btn qty-pill-btn--minus"
                              onClick={() => updateQuantity(productId, item.quantity - 1)}
                              disabled={loading || item.quantity <= 1}
                            >
                              <span aria-hidden="true">−</span>
                              <span className="visually-hidden">Decrease quantity</span>
                            </button>
                            <span className="qty-pill-value">{item.quantity}</span>
                            <button
                              type="button"
                              className="qty-pill-btn qty-pill-btn--plus"
                              onClick={() => updateQuantity(productId, item.quantity + 1)}
                              disabled={loading}
                            >
                              <span aria-hidden="true">+</span>
                              <span className="visually-hidden">Increase quantity</span>
                            </button>
                          </div>
                          <button
                            className="cart-remove-btn"
                            onClick={() => removeFromCart(productId)}
                            disabled={loading}
                            title="Remove item"
                          >
                            <i className="bi bi-trash"></i>
                          </button>

                          <button
                            className="cart-remove-btn"
                            onClick={() => {
                              moveCartToWishlist(productId).then((result) => {
                                if (!result?.success) showError(result?.message || 'Failed to save for later');
                                else showSuccess('Saved for later');
                              });
                            }}
                            disabled={loading}
                            title="Save for later"
                            aria-label="Save for later"
                          >
                            <i className="bi bi-heart"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {cart.items.length > 0 && (
          <div className="cart-drawer-footer">
            <div className="cart-drawer-summary">
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Subtotal</span>
                <span className="fw-bold">£{cart.total.toFixed(2)}</span>
              </div>
              <p className="text-muted small mb-3">Shipping & taxes calculated at checkout.</p>
            </div>
            
            <button 
              className="btn ah-btn-primary w-100 mb-2 py-3 fw-bold" 
              onClick={handleCheckout}
            >
              Checkout — £{cart.total.toFixed(2)}
            </button>
            <button 
              className="btn btn-outline-secondary w-100" 
              onClick={handleViewCart}
            >
              View Full Cart
            </button>
          </div>
        )}
      </div>
    </>
  );
}

export default CartDrawer;
